import { useId, useRef, type ClipboardEvent, type KeyboardEvent } from 'react'
import { cn } from '../cn.js'

export interface OtpInputProps {
  value: string
  onChange: (value: string) => void
  length?: number
  label?: string
  error?: string
  disabled?: boolean
  /** Fired when the last cell is filled — lets the caller auto-submit. */
  onComplete?: (value: string) => void
}

/**
 * Six-cell one-time-code input (PLAN.md §11.5).
 *
 * The details that decide whether this works on a phone:
 *   - `autoComplete="one-time-code"` so iOS offers the SMS/email code
 *   - `inputMode="numeric"` for the numeric keypad
 *   - paste-aware: users paste the whole code, and per-cell inputs normally
 *     drop everything after the first character
 *   - Backspace on an empty cell steps back, which is what people expect
 */
export function OtpInput({
  value,
  onChange,
  length = 6,
  label = 'Verification code',
  error,
  disabled,
  onComplete,
}: OtpInputProps) {
  const id = useId()
  const errorId = `${id}-error`
  const refs = useRef<Array<HTMLInputElement | null>>([])

  const digits = value.padEnd(length, ' ').slice(0, length).split('')

  const commit = (next: string) => {
    const clean = next.replace(/\D/g, '').slice(0, length)
    onChange(clean)
    if (clean.length === length) onComplete?.(clean)
  }

  const setAt = (i: number, char: string) => {
    const chars = value.padEnd(length, ' ').split('')
    chars[i] = char
    commit(chars.join('').replace(/ /g, ''))
    if (char && i < length - 1) refs.current[i + 1]?.focus()
  }

  const onKeyDown = (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i]?.trim() && i > 0) {
      e.preventDefault()
      refs.current[i - 1]?.focus()
      setAt(i - 1, '')
    }
    if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus()
    if (e.key === 'ArrowRight' && i < length - 1) refs.current[i + 1]?.focus()
  }

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    // Without this, pasting "123456" fills one cell with "1" and looks broken.
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (!pasted) return
    commit(pasted)
    refs.current[Math.min(pasted.length, length - 1)]?.focus()
  }

  return (
    <fieldset className="flex flex-col gap-1.5" disabled={disabled}>
      <legend className="text-sm font-medium text-ink">{label}</legend>

      <div className="flex gap-2" role="group" aria-describedby={error ? errorId : undefined}>
        {Array.from({ length }, (_, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el
            }}
            value={digits[i]?.trim() ?? ''}
            onChange={(e) => setAt(i, e.target.value.replace(/\D/g, '').slice(-1))}
            onKeyDown={onKeyDown(i)}
            onPaste={onPaste}
            onFocus={(e) => e.target.select()}
            inputMode="numeric"
            // Only the first cell carries this: iOS fills the whole value into
            // it, and our paste/commit logic spreads it across the rest.
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            aria-label={`Digit ${i + 1} of ${length}`}
            aria-invalid={error ? true : undefined}
            className={cn(
              'h-14 w-full rounded-lg border text-center text-xl font-semibold text-ink',
              error ? 'border-danger' : 'border-line-strong',
              'disabled:bg-surface-alt',
            )}
          />
        ))}
      </div>

      {error && (
        <p id={errorId} role="alert" className="flex items-start gap-1.5 text-sm text-danger">
          <span aria-hidden="true">⚠</span>
          <span>{error}</span>
        </p>
      )}
    </fieldset>
  )
}
